/**
 * Chat history storage management utilities
 * Handles localStorage operations for chat messages with analysis and cleanup
 */

const CHAT_MESSAGES_KEY = 'chatMessages';

/**
 * Get chat messages from localStorage
 * @returns {Array} Array of chat message objects
 */
export function getChatMessages() {
  try {
    const messages = localStorage.getItem(CHAT_MESSAGES_KEY);
    return messages ? JSON.parse(messages) : [];
  } catch (error) {
    console.warn('Failed to parse chat messages:', error);
    return [];
  }
}

/**
 * Clear all chat messages from localStorage
 * @returns {number} Number of messages cleared
 */
export function clearChatHistory() {
  try {
    const messages = getChatMessages();
    const messageCount = messages.length;
    localStorage.removeItem(CHAT_MESSAGES_KEY);
    return messageCount;
  } catch (error) {
    console.warn('Failed to clear chat history:', error);
    return 0;
  }
}

/**
 * Estimate token count for a message (rough approximation)
 * @param {string} text - Text to estimate tokens for
 * @returns {number} Estimated token count
 */
function estimateTokens(text) {
  if (!text || typeof text !== 'string') return 0;
  // Rough approximation: ~4 characters per token for English text
  return Math.ceil(text.length / 4);
}

/**
 * Get comprehensive chat history statistics
 * @returns {Object} Chat history statistics
 */
export function getChatHistoryStats() {
  try {
    const messages = getChatMessages();
    
    if (messages.length === 0) {
      return {
        totalMessages: 0,
        userMessages: 0,
        botMessages: 0,
        totalTokens: 0,
        userTokens: 0,
        botTokens: 0,
        storageSize: 0,
        storageSizeKB: 0,
        oldestMessage: null,
        newestMessage: null,
        uniqueLLMs: [],
        messagesPerDay: {}
      };
    }

    let userMessages = 0;
    let botMessages = 0;
    let userTokens = 0;
    let botTokens = 0;
    const uniqueLLMs = new Set();
    const messagesPerDay = {};

    // Analyze each message
    messages.forEach(message => {
      const tokens = estimateTokens(message.msg);
      
      if (message.user === 'user') {
        userMessages++;
        userTokens += tokens;
      } else {
        botMessages++;
        botTokens += tokens;
        if (message.llm) {
          uniqueLLMs.add(message.llm);
        }
      }

      // Group by day for activity analysis
      if (message.timestamp) {
        const date = new Date(message.timestamp).toDateString();
        messagesPerDay[date] = (messagesPerDay[date] || 0) + 1;
      }
    });

    // Calculate storage size
    const storageString = JSON.stringify(messages);
    const storageSize = new Blob([storageString]).size;

    // Get date range
    const timestamps = messages
      .map(m => m.timestamp)
      .filter(Boolean)
      .map(t => new Date(t))
      .sort((a, b) => a - b);

    return {
      totalMessages: messages.length,
      userMessages,
      botMessages,
      totalTokens: userTokens + botTokens,
      userTokens,
      botTokens,
      storageSize,
      storageSizeKB: Math.round(storageSize / 1024 * 100) / 100,
      oldestMessage: timestamps.length > 0 ? timestamps[0] : null,
      newestMessage: timestamps.length > 0 ? timestamps[timestamps.length - 1] : null,
      uniqueLLMs: Array.from(uniqueLLMs),
      messagesPerDay,
      averageUserTokensPerMessage: userMessages > 0 ? Math.round(userTokens / userMessages) : 0,
      averageBotTokensPerMessage: botMessages > 0 ? Math.round(botTokens / botMessages) : 0
    };
  } catch (error) {
    console.warn('Failed to get chat history stats:', error);
    return {
      totalMessages: 0,
      userMessages: 0,
      botMessages: 0,
      totalTokens: 0,
      userTokens: 0,
      botTokens: 0,
      storageSize: 0,
      storageSizeKB: 0,
      oldestMessage: null,
      newestMessage: null,
      uniqueLLMs: [],
      messagesPerDay: {}
    };
  }
}

/**
 * Get recent chat activity summary
 * @param {number} days - Number of days to look back (default: 7)
 * @returns {Object} Recent activity summary
 */
export function getRecentActivity(days = 7) {
  try {
    const messages = getChatMessages();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    const recentMessages = messages.filter(message => {
      if (!message.timestamp) return false;
      return new Date(message.timestamp) >= cutoffDate;
    });

    const userMessages = recentMessages.filter(m => m.user === 'user').length;
    const botMessages = recentMessages.filter(m => m.user !== 'user').length;

    return {
      totalMessages: recentMessages.length,
      userMessages,
      botMessages,
      days
    };
  } catch (error) {
    console.warn('Failed to get recent activity:', error);
    return {
      totalMessages: 0,
      userMessages: 0,
      botMessages: 0,
      days
    };
  }
}
